import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, CheckCircle2, XCircle } from "lucide-react";

const VCFG = {
  APROVADO: { cor: "bg-green-100 text-green-700 border-green-300", icon: ShieldCheck },
  REPROVADO: { cor: "bg-red-100 text-red-700 border-red-300", icon: ShieldAlert },
  "EM ANALISE": { cor: "bg-amber-100 text-amber-700 border-amber-300", icon: ShieldQuestion },
  "FORA DO SEGMENTO": { cor: "bg-slate-200 text-slate-700 border-slate-300", icon: ShieldQuestion },
};

const ST_CFG = {
  OK: "bg-green-100 text-green-700",
  FALHA: "bg-red-100 text-red-700",
  PENDENTE: "bg-amber-100 text-amber-700",
};

function operadorAtual() {
  try { return JSON.parse(localStorage.getItem("usuario") || "{}")?.nome || ""; } catch { return ""; }
}

// Pior veredito agrega o sinal do cliente (REPROVADO > EM ANALISE > APROVADO)
function agregar(analises) {
  if (analises.some((a) => a.veredito === "REPROVADO")) return "REPROVADO";
  if (analises.some((a) => a.veredito === "EM ANALISE")) return "EM ANALISE";
  if (analises.some((a) => a.veredito === "APROVADO")) return "APROVADO";
  return "EM ANALISE";
}

export default function AnaliseCreditoAuto({ clienteId, instituicao = "", perBank = false, className = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!clienteId) { setData(null); return; }
    let cancel = false;
    setLoading(true);
    axios
      .post(`${API}/regras-credito/analisar-cliente`, { cliente_id: clienteId, operador: operadorAtual(), instituicao })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [clienteId, instituicao]);

  if (!clienteId) return null;

  if (loading)
    return (
      <Badge className={`gap-1 bg-slate-100 text-slate-500 ${className}`} data-testid="analise-credito-auto-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Analisando crédito...
      </Badge>
    );

  const temOperacao = (data?.contratos_operacao?.length || 0) > 0;
  const temAnalise = (data?.analises?.length || 0) > 0;

  if (!data || (!temAnalise && !temOperacao))
    return (
      <Badge variant="outline" className={`gap-1 text-slate-500 ${className}`} data-testid="analise-credito-auto-semregra" title="Nenhuma regra de crédito nem parâmetro de operação cadastrado para este cliente">
        <ShieldQuestion className="h-3 w-3" /> Crédito: sem regra
      </Badge>
    );

  const ver = temAnalise ? agregar(data.analises) : null;
  const cfg = ver ? (VCFG[ver] || VCFG["EM ANALISE"]) : { cor: "bg-indigo-100 text-indigo-700 border-indigo-300", icon: ShieldCheck };
  const Icon = cfg.icon;
  const aptos = data.resumo?.contratos_aptos || 0;
  const inaptos = data.resumo?.contratos_inaptos || 0;
  const portaveis = data.resumo_operacao?.portaveis || 0;
  const refinanciaveis = data.resumo_operacao?.refinanciaveis || 0;
  const bancosTxt = [...new Set((data.analises || []).map((a) => a.banco_nome).filter(Boolean))].join(", ");

  return (
    <>
      {perBank ? (
        <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid="analise-credito-auto-perbank">
          {data.analises.map((a, i) => {
            const c = VCFG[a.veredito] || VCFG["EM ANALISE"];
            const I = c.icon;
            const apt = (a.contratos || []).filter((x) => x.apto).length;
            const tot = (a.contratos || []).length;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(true)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors hover:opacity-80 ${c.cor}`}
                data-testid={`credito-banco-${i}`}
                title={`${a.banco_nome}: ${a.veredito} — clique para detalhes`}
              >
                <I className="h-3.5 w-3.5" />
                {a.banco_nome}: {a.veredito}
                {tot > 0 && <span className="opacity-80">· {apt}/{tot} aptos</span>}
              </button>
            );
          })}
          {!temAnalise && temOperacao && (
            <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold bg-indigo-100 text-indigo-700 border-indigo-300 hover:opacity-80" data-testid="credito-operacoes">
              <ShieldCheck className="h-3.5 w-3.5" /> Operações · {portaveis} port · {refinanciaveis} refin
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors hover:opacity-80 ${cfg.cor} ${className}`}
          data-testid="analise-credito-auto-badge"
          title="Ver detalhes da análise de crédito"
        >
          <Icon className="h-3.5 w-3.5" />
          Crédito: {ver || "operações"}
          {bancosTxt && <span className="opacity-80" data-testid="analise-credito-auto-banco">· {bancosTxt}</span>}
          {ver && (aptos + inaptos) > 0 && <span className="opacity-80">· {aptos}/{aptos + inaptos} aptos</span>}
          {!ver && temOperacao && <span className="opacity-80">· {portaveis} port · {refinanciaveis} refin</span>}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="analise-credito-auto-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" /> Análise de Crédito — {data.cliente_nome}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
              <span><b>Idade:</b> {data.idade ?? "-"}</span>
              <span><b>Vínculo:</b> {data.vinculo || "-"}</span>
            </div>

            {data.analises.map((a, idx) => {
              const c = VCFG[a.veredito] || VCFG["EM ANALISE"];
              return (
                <div key={idx} className="rounded-lg border p-3 space-y-2" data-testid={`analise-regra-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-700">{a.banco_nome} <span className="text-slate-400">·</span> {a.orgao_nome}</div>
                    <Badge className={`${c.cor} border`}>{a.veredito}</Badge>
                  </div>
                  {a.regras_reprovadas?.length > 0 && (
                    <p className="text-xs text-red-600" data-testid={`analise-reprovou-${idx}`}>
                      <b>Reprovado em:</b> {a.regras_reprovadas.join(", ")}
                    </p>
                  )}

                  {a.criterios?.length > 0 && (
                    <div data-testid={`analise-checklist-${idx}`}>
                      <p className="text-xs font-semibold text-slate-600 mb-1">Checklist das regras (comparativo)</p>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Critério (regra)</TableHead>
                            <TableHead className="text-xs">Esperado</TableHead>
                            <TableHead className="text-xs">Cliente</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {a.criterios.map((cr, i) => (
                            <TableRow key={i} data-testid={`criterio-${idx}-${i}`}>
                              <TableCell className="text-xs font-medium">{cr.nome}</TableCell>
                              <TableCell className="text-xs text-slate-500">{cr.esperado}</TableCell>
                              <TableCell className="text-xs">{String(cr.informado)}</TableCell>
                              <TableCell><Badge className={`text-xs ${ST_CFG[cr.status] || "bg-slate-100 text-slate-600"}`}>{cr.status}</Badge></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {a.contratos?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Contrato</TableHead>
                          <TableHead className="text-xs">Parcela</TableHead>
                          <TableHead className="text-xs">Prazo</TableHead>
                          <TableHead className="text-xs">Apto?</TableHead>
                          <TableHead className="text-xs">Motivo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {a.contratos.map((ct, i) => (
                          <TableRow key={i} data-testid={`analise-contrato-${idx}-${i}`}>
                            <TableCell className="text-xs font-mono">{ct.numero_contrato || "-"}</TableCell>
                            <TableCell className="text-xs">R$ {Number(ct.valor_parcela || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-xs">{ct.quantidade_parcelas || "-"}</TableCell>
                            <TableCell>
                              {ct.apto ? (
                                <Badge className="bg-green-100 text-green-700 gap-1 text-xs"><CheckCircle2 className="h-3 w-3" /> Apto</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 gap-1 text-xs"><XCircle className="h-3 w-3" /> Inapto</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">{ct.motivo}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-xs text-slate-400">Sem contratos deste banco para avaliar.</p>
                  )}
                </div>
              );
            })}

            {data.contratos_operacao?.length > 0 && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="operacao-contratos">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-blue-700">Portabilidade & Refinanciamento (parâmetros do banco)</p>
                  <span className="text-xs text-blue-600">{data.resumo_operacao?.portaveis || 0} portáveis · {data.resumo_operacao?.refinanciaveis || 0} refinanciáveis</span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Contrato</TableHead>
                      <TableHead className="text-xs">Banco</TableHead>
                      <TableHead className="text-xs">Parcela</TableHead>
                      <TableHead className="text-xs">Pode Portar?</TableHead>
                      <TableHead className="text-xs">Pode Refinanciar?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.contratos_operacao.map((ct, i) => (
                      <TableRow key={i} data-testid={`operacao-contrato-${i}`}>
                        <TableCell className="text-xs font-mono">{ct.numero_contrato || "-"}</TableCell>
                        <TableCell className="text-xs">{ct.banco_nome}</TableCell>
                        <TableCell className="text-xs">R$ {Number(ct.valor_parcela || 0).toFixed(2)}</TableCell>
                        <TableCell><OpBadge op={ct.port} /></TableCell>
                        <TableCell><OpBadge op={ct.refin} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-[11px] text-slate-400">Parâmetros cadastrados em Bancos → Parâmetros (mesma base do Monitoramento).</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OpBadge({ op }) {
  if (!op) return <span className="text-xs text-slate-400">sem parâmetro</span>;
  if (op.pode)
    return <Badge className="bg-green-100 text-green-700 gap-1 text-xs"><CheckCircle2 className="h-3 w-3" /> Pode</Badge>;
  return (
    <Badge className="bg-red-100 text-red-700 gap-1 text-xs" title={`Reprovado: ${(op.reprovou || []).join(", ")}`}>
      <XCircle className="h-3 w-3" /> Não{op.reprovou?.length ? ` (${op.reprovou.join(", ")})` : ""}
    </Badge>
  );
}
